import { useTranslations } from 'next-intl';
import { Fragment, type ReactNode, useMemo, useState } from 'react';
import { BaseLineCheck2 } from '@/app/components/icons/BaseLineCheck2';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { StarIcon } from '@/app/components/icons/StarIcon';
import { ViduLogo2 } from '@/app/components/icons/ViduLogo2';
import { CouponBadge } from '@/app/components/shared/CouponBadge';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { getCreditsDiscount } from '@/app/components/shared/PlanDetail/utils';
import { CURRENCY, SITE } from '@/app/constants';
import { useUserNexus } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ClawPassProduct } from '@/app/types';
import { cn, formatAmount, formatPrice, formatPriceDiscount, getPriceDiscount, hasSelectedCoupon } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import { BaseFillIntelligentDuration } from '../../icons/BaseFillIntelligentDuration';
import { CLAW_PASS_BUNDLE_RIGHTS, CLAW_PASS_RIGHTS, type ClawPassPlanType } from './constants';
import { computeClawPassTotalCents } from './utils';

const QUANTITY_MIN = 1;
const QUANTITY_MAX = 12;

const CREDITS_CONFIG_LIST: Record<
  ClawPassPlanType,
  {
    credits: number;
    mins: number;
    videoCount: number;
    creditsColor: string;
  }
> = {
  claw_pass_basic: {
    credits: 800,
    mins: 10,
    videoCount: 100,
    creditsColor: 'bg-[linear-gradient(270deg,#BA9D64_0%,#FFF7D0_100%)]',
  },
  claw_pass_pro: {
    credits: 800,
    mins: 40,
    videoCount: 400,
    creditsColor: 'bg-[linear-gradient(90deg,#FFA968_0%,#D7672A_86.51%)]',
  },
};

const PLAN_TITLE_STYLE_MAP: Record<ClawPassPlanType, React.CSSProperties> = {
  claw_pass_basic: {
    color: '#fff',
  },
  claw_pass_pro: {
    backgroundImage: 'linear-gradient(90deg, #FFE2DD 2.778%, #D7672A 95.833%)',
  },
};

const ClawPassStepperMinusIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M4.16667 10H15.8333"
      stroke="currentColor"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ClawPassStepperPlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M4.16667 10H15.8333"
      stroke="currentColor"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 4.16667V15.8333"
      stroke="currentColor"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ClawRightItem = ({ primary, secondaries }: { primary: ReactNode; secondaries?: ReactNode[] }) => (
  <div className="flex flex-col gap-1.5">
    <div className="flex items-start justify-start gap-2">
      <div className="flex h-5 items-center justify-center">
        <BaseLineCheck2 className="size-4 shrink-0 text-[#888888]" />
      </div>
      <div className="flex flex-col gap-1">
        <span className="font-normal text-system-white">{primary}</span>
        {secondaries?.map((secondary) => (
          <span key={secondary as string} className="text-system-white40">
            {secondary}
          </span>
        ))}
      </div>
    </div>
  </div>
);

export type ClawPassProductType = 'claw_pass_bundle' | 'claw_pass';

const TogglePickIcon = ({ className, checked }: { className?: string; checked: boolean }) => (
  <svg className={className} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    {checked ? (
      <>
        <rect x="1.75" y="1.75" width="10.5" height="10.5" rx="5.25" fill="currentColor" stroke="currentColor" />
        <path
          d="M4.6 7.1 6.2 8.7 9.5 5.3"
          stroke="#1c1410"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.7"
        />
      </>
    ) : (
      <rect x="1.75" y="1.75" width="10.5" height="10.5" rx="5.25" stroke="currentColor" strokeWidth="1.3" />
    )}
  </svg>
);

const ProductTypeToggle = ({
  activeType,
  onChange,
  isPro,
}: {
  activeType: ClawPassProductType;
  onChange: (next: ClawPassProductType) => void;
  isPro: boolean;
}) => {
  const t = useTranslations();
  const checked = activeType === 'claw_pass_bundle';
  return (
    <button
      type="button"
      aria-pressed={checked}
      onClick={() => onChange(checked ? 'claw_pass' : 'claw_pass_bundle')}
      className={cn(
        'group absolute inset-x-0 top-0 z-0 flex items-center justify-center gap-1 overflow-hidden rounded-t-[12px] px-2 pt-1 pb-4 transition-colors',
        isPro
          ? 'bg-[rgba(255,109,30,0.16)] text-[#ff7024] hover:bg-[rgba(255,109,30,0.22)]'
          : 'bg-system-white16 text-system-white64 hover:bg-system-white24',
      )}
      aria-label={t('payments.plans.passToggleAria')}
    >
      <TogglePickIcon className="size-3.5 shrink-0 opacity-40" checked={checked} />
      <span className="font-normal text-sm leading-[1.4] opacity-[0.64]">{t('payments.plans.bundleLabel')}</span>
    </button>
  );
};

interface PassCardProps {
  productGroup: ClawPassProduct[];
  renderSubscriptionButton: (product: ClawPassProduct, quantity: number) => React.ReactNode;
  /** 全局选中的 product_type（整体切换：所有卡片同步） */
  activeType: ClawPassProductType;
  /** 切换 product_type，回写到父级全局状态 */
  onTypeChange: (next: ClawPassProductType) => void;
}

export const PassCard = ({ productGroup, renderSubscriptionButton, activeType, onTypeChange }: PassCardProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const isFreeUser = useStore((s) => s.userInfo?.subs_plan === 'free');
  const { isClawPassBundleFirstPurchase } = useUserNexus();
  const [quantity, setQuantity] = useState(1);

  // 同档位下的两种商品（claw_pass / claw_pass_bundle）。后端可能只返回一种。
  const bundleProduct = useMemo(() => productGroup.find((p) => p.product_type === 'claw_pass_bundle'), [productGroup]);
  const passProduct = useMemo(() => productGroup.find((p) => p.product_type === 'claw_pass'), [productGroup]);
  const canToggle = Boolean(bundleProduct && passProduct);

  // 跟随全局 activeType；若本档位缺少该类型则回退到另一种，保证始终有 product 可渲染
  const product = activeType === 'claw_pass_bundle' ? (bundleProduct ?? passProduct) : (passProduct ?? bundleProduct);
  if (!product) return null;

  const planType = product.claw_pass_plan_type as ClawPassPlanType;
  const creditsConfig = CREDITS_CONFIG_LIST[planType];
  if (!creditsConfig) return null;

  const creditsDiscount = getCreditsDiscount(product.plan_type, locale);

  // 总价：第一份按 price、其余份按 original_price（首购券仅作用于第一个月，其余按原价）
  const totalPriceCents = computeClawPassTotalCents(product, quantity);
  // 数量选择器：1-12 月，仅当卡片可购买时启用
  const isAvailable = product.product_pay_status === 'available';

  const translateRightKey = (key: string): ReactNode => {
    switch (key) {
      case 'claw_includesAllPlanBenefits':
        return t(`payments.plans.${key}`, { plan: t(`payments.${product.plan_type}`) });
      case 'claw_monthlyCredits':
        return t.rich(`payments.plans.${key}`, { credits: creditsConfig.credits, value: (chunks) => <>{chunks}</> });
      case 'claw_creditsPurchaseDiscount':
        return t(`payments.plans.${key}`, { discount: creditsDiscount });
      default:
        return t(`payments.plans.${key}`);
    }
  };

  const rights = product.product_type === 'claw_pass_bundle' ? CLAW_PASS_BUNDLE_RIGHTS : CLAW_PASS_RIGHTS;
  const isPro = planType === 'claw_pass_pro';
  const showFirstPurchase =
    isFreeUser && isClawPassBundleFirstPurchase && SITE === 'china' && product.product_type === 'claw_pass_bundle';
  // 若后端给了折扣价（price < original_price），追加 "X 折/X% off"
  const firstPurchaseDiscount = getPriceDiscount(product.price, product.original_price ?? 0);
  // 当用户选中可用优惠券时，套餐名称区域以「优惠券折扣」标签替代首购折扣标签
  const hasCoupon = hasSelectedCoupon(product);
  const monthsInPeriod = product.billing_period === 'yearly' ? 12 : 1;

  return (
    <div
      className={cn(
        'relative flex h-full w-[340px] flex-col items-center justify-start overflow-hidden sm:w-full',
        isPro && 'rounded-16 bg-[#D7672A]',
      )}
    >
      <div className="flex h-11 shrink-0 items-center justify-center">
        {isPro && (
          <div className="flex items-center gap-1 font-medium text-sm text-white leading-5">
            <StarIcon className="size-[14px] shrink-0" />
            <span>{t('payments.plans.bestValue')}</span>
          </div>
        )}
      </div>

      <div
        className={cn(
          'relative flex h-full w-full flex-col justify-start overflow-hidden rounded-16 border-2 border-system-white16 px-4 py-6 text-system-white',
          planType === 'claw_pass_basic' &&
            'border-[rgba(250,241,200,0.16)] bg-[#12100d] shadow-[inset_0_0_100px_0_rgba(250,241,200,0.08)]',
          isPro &&
            'border-[#D7502A] bg-[linear-gradient(180deg,#161722_0%,rgba(30,30,30,0.8)_100%)] shadow-[inset_0_0_100px_0_rgba(215,103,42,0.4)]',
        )}
      >
        {/* 套餐名称 */}
        <div className="border-system-white12 border-b pb-4 font-semibold text-2xl">
          <div className="flex flex-wrap items-center gap-2">
            <div
              className={cn(
                'flex flex-wrap items-baseline gap-2 font-semibold text-2xl leading-[1.4]',
                isPro && 'bg-clip-text text-transparent',
              )}
              style={PLAN_TITLE_STYLE_MAP[planType]}
            >
              <span>{t(`payments.${planType}`)}</span>
              {product.product_type === 'claw_pass_bundle' && (
                <span className="font-normal text-base">{t('payments.claw_pass_bundle_suffix')}</span>
              )}
            </div>
            {hasCoupon ? (
              <CouponBadge discount={firstPurchaseDiscount} />
            ) : showFirstPurchase && firstPurchaseDiscount > 0 ? (
              <div className="flex items-center gap-1 rounded-8 bg-[linear-gradient(90deg,#FF7678_0%,#FF383B_100%)] px-2 py-1 font-bold text-white text-xs uppercase italic leading-[1.4]">
                <span>
                  {t('payments.plans.discount', {
                    discount: formatPriceDiscount(firstPurchaseDiscount, locale),
                  })}
                </span>
              </div>
            ) : null}
          </div>
          <div className="mt-2 font-normal text-sm text-system-white40 leading-5">
            {t('payments.claw_pass_subtitle')}
          </div>
        </div>

        {/* 价格 */}
        <div className="py-6 text-sm text-system-white">
          <div className="flex items-end gap-1 leading-none">
            <span className="font-semibold text-2xl text-system-white40 leading-[1.4]">{CURRENCY}</span>
            <span className="font-semibold text-[48px] leading-none">
              {formatAmount(product.price / monthsInPeriod)}
            </span>
            <span className="text-base text-system-white16 leading-[1.4]">/</span>
            <span className="text-base text-system-white40 leading-[1.4]">{t('payments.per_monthly')}</span>
            {product.original_price && firstPurchaseDiscount > 0 ? (
              <span className="text-base text-system-white40 leading-[1.4] line-through">
                {formatPrice(product.original_price / monthsInPeriod)}
              </span>
            ) : null}
          </div>
        </div>

        {isAvailable && (
          <div className="mb-3 flex h-[52px] w-full items-center gap-4 rounded-[100px] bg-system-white08 pr-3 pl-2">
            <div className="flex h-full w-[140px] shrink-0 items-center">
              <button
                type="button"
                className="flex size-9 shrink-0 items-center justify-center rounded-[100px] bg-system-white08 text-system-white disabled:text-system-white24"
                onClick={() => setQuantity((q) => Math.max(QUANTITY_MIN, q - 1))}
                disabled={quantity <= QUANTITY_MIN}
              >
                <ClawPassStepperMinusIcon className="size-5 shrink-0" />
              </button>
              <div className="flex h-full min-w-px flex-1 items-center justify-center px-4 py-2">
                <span className="font-medium text-[20px] text-system-white leading-[1.4]">{quantity}</span>
              </div>
              <button
                type="button"
                className="flex size-9 shrink-0 items-center justify-center rounded-[100px] bg-system-white08 text-system-white disabled:text-system-white24"
                onClick={() => setQuantity((q) => Math.min(QUANTITY_MAX, q + 1))}
                disabled={quantity >= QUANTITY_MAX}
              >
                <ClawPassStepperPlusIcon className="size-5 shrink-0" />
              </button>
            </div>
            <div className="h-8 w-px shrink-0 bg-system-white12" aria-hidden />
            <div className="flex h-full shrink-0 items-center gap-2 py-2">
              <span className="shrink-0 font-normal text-[14px] text-system-white leading-[1.4] opacity-40">
                {t('payments.plans.claw_total')}
              </span>
              <span className="shrink-0 font-medium text-[20px] text-system-white tabular-nums leading-[1.4]">
                {formatPrice(totalPriceCents)}
              </span>
            </div>
          </div>
        )}

        {renderSubscriptionButton && (
          <div className="mb-5 flex flex-col gap-2">
            <div className={cn('relative', canToggle && 'pt-7')}>
              {canToggle && <ProductTypeToggle activeType={activeType} onChange={onTypeChange} isPro={isPro} />}
              <div className="relative z-10">{renderSubscriptionButton(product, quantity)}</div>
            </div>
            {showFirstPurchase && (
              <p className="text-center font-normal text-sm text-system-white/40 leading-5">
                {t('payments.plans.claw_first_purchase_desc', {
                  firstPrice: formatPrice(product.price),
                  originalPrice: formatPrice(product.original_price ?? 0),
                })}
              </p>
            )}
          </div>
        )}

        <div className="relative mb-5 flex flex-col items-center gap-4 overflow-hidden rounded-16 bg-black/20 p-4">
          <ViduLogo2 className="-right-[17px] absolute top-[69px] h-[70px] w-[85px] [&_g]:opacity-[0.05]" />
          <div
            className={cn('relative z-10 flex w-full items-start', isFreeUser ? 'justify-between' : 'justify-center')}
          >
            <div className="flex w-[125px] flex-col items-center gap-1 text-center text-white text-xs leading-5">
              {t.rich('payments.plans.claw_dailyCompute', {
                mins: creditsConfig.mins,
                value: (chunks) => (
                  <span
                    className={cn(
                      creditsConfig.creditsColor,
                      'block bg-clip-text font-semibold text-transparent text-xl leading-none',
                    )}
                  >
                    {chunks}
                  </span>
                ),
              })}
            </div>
            {isFreeUser && (
              <>
                <div className="relative flex items-center">
                  <div className="h-16 border-system-white16 border-l" />
                  <BaseFillIntelligentDuration
                    className={cn(
                      '-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 size-5',
                      isPro ? 'text-[#D7672A]' : 'text-[#BA9D64]',
                    )}
                  />
                </div>
                <div className="flex w-[125px] flex-col items-center gap-1 text-center text-white text-xs leading-5">
                  {t.rich('payments.plans.claw_monthlyCredits', {
                    credits: creditsConfig.credits,
                    value: (chunks) => (
                      <span
                        className={cn(
                          creditsConfig.creditsColor,
                          'block bg-clip-text font-semibold text-transparent text-xl leading-none',
                        )}
                      >
                        {chunks}
                      </span>
                    ),
                  })}
                </div>
              </>
            )}
          </div>
          <div className="relative z-10 flex items-center gap-1 text-center text-system-white40 text-xs leading-5">
            {t.rich('payments.plans.claw_approxVideos', {
              count: creditsConfig.videoCount,
              value: (chunks) => (
                <span className={cn(creditsConfig.creditsColor, 'bg-clip-text text-transparent')}>{chunks}</span>
              ),
            })}
            <CustomTooltip
              content={t('payments.plans.claw_creditsGenerateTips')}
              trigger="auto"
              disableHoverableContent={true}
              container={document.body}
            >
              <BaseLineExclamation className="inline shrink-0 cursor-pointer align-text-bottom text-sm text-system-white40" />
            </CustomTooltip>
          </div>
        </div>

        {/* 权益列表 */}
        <div className="mb-3 flex flex-col gap-3 text-left text-sm text-system-white">
          {rights.map((entry, idx) => (
            <Fragment key={`${entry.primaryKey}-${idx}`}>
              {entry.divider && <div className="h-px w-full bg-system-white08" />}
              <ClawRightItem
                primary={translateRightKey(entry.primaryKey)}
                secondaries={entry.secondaryKeys?.map(translateRightKey)}
              />
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};
